import FormFieldComponent from '@/components/FormFieldComponent';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useEffect, useMemo, useState } from 'react';
import FormFieldCalendar from '@/components/FormFieldCalendar';
import { FormFieldSelect } from '@/components/FormFieldSelect';
import FormFieldSearch from '@/components/FormFieldSearch';
import FormFieldPhone from '@/components/FormFieldPhone';
import { courtSearchForCaseType, useCaseTypes } from '@/services/caseTypeCourtMappingService';
import {
  HEARING_DATE_CASE_TYPE,
  FAX_REPLACE_REGEX,
  JURISDICTION_OPTIONS,
  CITY_VALIDATION,
  STATE_VALIDATION,
  ADDRESS_LINE_VALIDATION,
  PROPERTY_ADDRESS_CASE_TYPE,
  ZIP_CODE_VALIDATION,
  RUSH_TYPE_OPTIONS,
} from '@/utils/constant';
import FormFieldCheckbox from '@/components/FormFieldCheckbox';
import { CreateCourtModal } from '@pages/courts/CreateCourtModal';
import Loader from '@/components/Loader';
import { useClientBillingPreferenceGet } from '@/services/clients';
import { InvoiceType } from '@/types/accountsReceivable.types';
import { toast } from '@/components/ui/use-toast';
import { FormFieldRadioGroup } from '@/components/FormFieldRadioGroup';
import { CaseResponse, RushType } from '@/types/case.types';
import { hasFeature } from '@/services/featureService';
import { getPropertyStateForCaseType } from '../pe.schema';

export const isPrepaidFeatureEnabled = hasFeature('accounting.accountsReceivable.prepaid');

const insuranceFieldsValidation = {
  maxLength: 128,
  allowNumber: false,
  allowSpecialCharacters: false,
};

export const PeCaseDetails = ({
  mode = 'CREATE',
  existingCaseDetails,
}: {
  mode?: 'CREATE' | 'UPDATE';
  existingCaseDetails?: CaseResponse;
}) => {
  const { control, getValues, setValue, watch, unregister, clearErrors } = useFormContext();
  const [openInsuranceData, setOpenInsuranceData] = useState<boolean>(true);
  const watchCaseType = watch('case.typeId') as string;
  const watchHasInsurance = watch('case.hasInsurance') as boolean;
  const [addNewCourt, setAddNewCourt] = useState<boolean>(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [courtSelected, setCourtSelected] = useState<boolean>(false);

  const lockedPropertyState = getPropertyStateForCaseType(watchCaseType);

  useEffect(() => {
    if (lockedPropertyState && getValues('case.state') !== lockedPropertyState) {
      setValue('case.state', lockedPropertyState, { shouldDirty: true, shouldValidate: true });
    }
  }, [watchCaseType]);

  const rush = watch('case.rush') as RushType;
  const clientId = watch('clientId') as string;

  const { data: billingPreference, isLoading: isLoadingBillingPreference } = useClientBillingPreferenceGet(clientId, {
    enabled: mode === 'CREATE',
  });

  useEffect(() => {
    if (mode === 'CREATE' || existingCaseDetails?.rush !== rush) {
      const currentDate = new Date();
      if (rush) {
        if (rush === RushType.NEXT_DAY)
          setValue('case.serveBy', new Date(currentDate.setDate(currentDate.getDate() + 1)));
        else setValue('case.serveBy', new Date(currentDate.setDate(currentDate.getDate())));
      } else setValue('case.serveBy', new Date(currentDate.setDate(currentDate.getDate() + 5)));
    }
    if (mode === 'UPDATE' && existingCaseDetails?.rush === rush && existingCaseDetails.serveBy)
      setValue('case.serveBy', new Date(existingCaseDetails.serveBy));
  }, [rush]);

  useEffect(() => {
    if (clientId && mode === 'CREATE' && billingPreference?.data && isPrepaidFeatureEnabled) {
      const isPrepaid = billingPreference.data.billingType === InvoiceType.PREPAID;
      setValue('isPrepaid', isPrepaid);
    }
  }, [billingPreference]);

  const { data: caseType, isFetching: isFetchingCaseType } = useCaseTypes();

  const caseTypeOptions = useMemo(() => {
    if (caseType?.caseTypes)
      return caseType.caseTypes.map((item) => ({
        label: item.name,
        value: item.caseTypeId,
      }));
    return [];
  }, [caseType]);

  return (
    <Loader isLoading={isFetchingCaseType || isLoadingBillingPreference} className="min-h-[50vh]">
      <div className="flex flex-col w-full mt-11 gap-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-black">
          <FormFieldSelect
            name="case.jurisdiction"
            label="Jurisdiction"
            options={JURISDICTION_OPTIONS}
            required
            placeholder="Select Jurisdiction"
          />
          <FormFieldSelect
            name="case.typeId"
            label="Case Type"
            options={caseTypeOptions}
            required
            placeholder="Select Case Type"
            onChange={() => {
              unregister([
                'case.hearingOn',
                'case.address1',
                'case.address2',
                'case.city',
                'case.state',
                'case.zipCode',
              ]);
              if (mode === 'UPDATE') {
                toast({
                  description:
                    'Updating the case type will remove the selected options for affidavit type and descriptions of the document.',
                  variant: 'default',
                  duration: 5000,
                });
              }
              const workOrders = (getValues('workOrders') as []) || [];
              workOrders.forEach((_, index) => {
                unregister(`workOrders[${index}].affidavitTypeId`);
                unregister(`workOrders[${index}].documents`);
                // The Property Address was just cleared, so "Same as Property Address" no longer holds (DGR-1457).
                setValue(`workOrders[${index}].sameAsPropertyAddress`, false);
              });
            }}
          />
          <FormFieldSearch
            name="case.courtId"
            label="Court Name"
            placeholder="Search Court"
            key={refreshKey}
            props={{
              Icon: Search,
              iconProps: { style: { color: '#969696', height: '20px', width: '20px' } },
            }}
            required
            addNewOption={{
              label: 'Add New Court',
              onNewOptionClick: () => {
                setAddNewCourt(true);
              },
            }}
            onChange={() => setCourtSelected(true)}
            onSearchChange={() => setCourtSelected(false)}
            api={(params) => {
              const updatedParams = {
                ...params,
                pageSize: 100,
                search: params.search?.includes('-') && courtSelected ? params.search.split('-')[0] : params.search,
              };
              return courtSearchForCaseType(updatedParams, refreshKey, watchCaseType);
            }}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-black">
          <FormFieldComponent
            control={control}
            name="case.number"
            label="Case #"
            placeholder="Enter Case No"
            required
            maxLength={20}
            capitalize
            disabled={mode === 'UPDATE'}
            replaceRegex={/^(\/|-)|[^a-zA-Z0-9/-]/g}
          />
          <FormFieldComponent
            control={control}
            name="case.name"
            label="Case Name"
            placeholder="Enter Case Name"
            required
            maxLength={128}
          />
          <FormFieldComponent
            control={control}
            name="case.fileNo"
            label="File #"
            placeholder="Enter File Number"
            required
            replaceRegex={/^(\/|-)|[^a-zA-Z0-9/-]/g}
            capitalize
            maxLength={20}
          />
        </div>
        <div className="grid grid-cols-2 gap-6">
          <FormFieldComponent
            control={control}
            name="case.plaintiff"
            label="Plaintiff"
            placeholder="Enter Plaintiff"
            required
            allowNumber={false}
          />
          <FormFieldComponent
            control={control}
            name="case.defendant"
            label="Defendant"
            placeholder="Enter Defendant"
            required
            allowNumber={false}
          />
        </div>
        {PROPERTY_ADDRESS_CASE_TYPE.includes(watchCaseType) && (
          <div className="grid grid-cols-1 gap-6" key={watchCaseType}>
            <FormFieldComponent
              control={control}
              name="case.address1"
              label="Property Address Line 1"
              placeholder="Enter Street Address"
              required
              {...ADDRESS_LINE_VALIDATION}
            />
            <FormFieldComponent
              control={control}
              name="case.address2"
              label="Property Address Line 2"
              placeholder="Enter Apt, suite etc. (Optional)"
              {...ADDRESS_LINE_VALIDATION}
            />
            <div className="grid grid-cols-3 gap-6" key={watchCaseType}>
              <FormFieldComponent
                control={control}
                name="case.city"
                label="City"
                placeholder="City"
                required
                {...CITY_VALIDATION}
              />
              <FormFieldComponent
                control={control}
                name="case.state"
                label="State"
                placeholder="State"
                required
                disabled={!!lockedPropertyState}
                {...STATE_VALIDATION}
              />
              <FormFieldComponent
                control={control}
                name="case.zipCode"
                label="Zip Code"
                placeholder="Zip Code"
                required
                {...ZIP_CODE_VALIDATION}
              />
            </div>
          </div>
        )}

        <div className="text-md w-full flex flex-col gap-6">
          <div className="text-xl font-semibold">Dates</div>
          <div
            className={`grid grid-cols-1 md:grid-cols-2  gap-4 w-full
           `}
          >
            <FormFieldCalendar
              control={control}
              name="case.serveBy"
              label="Serve By"
              disabledAt={(date: Date) => date < new Date()}
              placeholder="Select Date"
              required
              fieldClassName="bg-[#F5F5F5] hover:bg-[#F5F5F5]"
              disabled={!!rush}
            />

            {(watchCaseType === HEARING_DATE_CASE_TYPE.SUB || watchCaseType === HEARING_DATE_CASE_TYPE.NY_LT) && (
              <FormFieldCalendar
                control={control}
                name="case.hearingOn"
                label="Hearing"
                disabledAt={(date: Date) => date < new Date()}
                required
                placeholder="Select Date"
              />
            )}
          </div>
        </div>
        <div className="w-full flex flex-col gap-6">
          <FormFieldRadioGroup
            label="Rush"
            name="case.rush"
            options={RUSH_TYPE_OPTIONS}
            direction="horizontal"
            optionsInNextLine
          />
          {isPrepaidFeatureEnabled && (
            <div className="!mx-[-8px] mt-[8px]">
              <FormFieldCheckbox control={control} name="isPrepaid" label="Pre-paid" />
            </div>
          )}
          <div className="!mx-[-8px]">
            <FormFieldCheckbox
              control={control}
              name="case.hasInsurance"
              label="Insurance Data"
              onCheckChange={() => {
                setValue('case.insurance', undefined);
                clearErrors('case.insurance');
              }}
            />
          </div>
          {watchHasInsurance && (
            <Collapsible
              className="w-[100%] bg-[#F5F5F5] flex flex-col rounded-md justify-between "
              open={openInsuranceData}
            >
              <CollapsibleTrigger
                className="flex justify-between items-center w-full bg-[#F5F5F5] py-3 px-4 rounded-md "
                onClick={() => {
                  setOpenInsuranceData(!openInsuranceData);
                }}
              >
                <span className="font-semibold ">Insurance Data</span>
                {openInsuranceData ? <ChevronUp color="#969696" /> : <ChevronDown color="#969696" />}
              </CollapsibleTrigger>

              <CollapsibleContent className="!rounded-md">
                <div className="flex flex-col p-4 gap-4">
                  <div className="grid grid-cols-1 md:grid-cols-3  w-full gap-6 text-black">
                    <FormFieldComponent
                      name="case.insurance.adjuster"
                      control={control}
                      label="Adjuster"
                      placeholder="Enter Adjuster"
                      props={{ className: 'bg-white' }}
                      {...insuranceFieldsValidation}
                    />
                    <FormFieldComponent
                      control={control}
                      name="case.insurance.adjusterName"
                      label="Adjuster's Name"
                      placeholder="Enter Adjuster's Name"
                      props={{ className: 'bg-white' }}
                      {...insuranceFieldsValidation}
                    />
                    <FormFieldComponent
                      control={control}
                      name="case.insurance.insuredName"
                      label="Insured Name"
                      placeholder="Enter Insured Name"
                      props={{ className: 'bg-white' }}
                      {...insuranceFieldsValidation}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 w-full gap-6 text-black">
                    <FormFieldPhone name="case.insurance.phone" label="Phone Number" defaultCountry="us" />
                    <FormFieldComponent
                      control={control}
                      name="case.insurance.fax"
                      label="Fax"
                      placeholder="Enter Fax"
                      props={{ className: 'bg-white' }}
                      maxLength={128}
                      replaceRegex={FAX_REPLACE_REGEX}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 w-full gap-6 text-black">
                    <FormFieldCalendar
                      control={control}
                      name="case.insurance.dateOfLoss"
                      label="Date of Loss"
                      placeholder="Select Date of Loss"
                      className="!bg-white"
                      disabledAt={(date: Date) => date > new Date()}
                    />
                    <FormFieldComponent
                      control={control}
                      name="case.insurance.billTo"
                      label="Bill To"
                      placeholder="Enter Bill To"
                      props={{ className: 'bg-white' }}
                      maxLength={128}
                      allowSpecialCharacters={false}
                    />
                    <FormFieldComponent
                      control={control}
                      name="case.insurance.policy"
                      label="Policy"
                      placeholder="Enter Policy"
                      props={{ className: 'bg-white' }}
                      maxLength={128}
                      replaceRegex={/[^a-zA-Z0-9/-]/g}
                    />
                    <FormFieldComponent
                      control={control}
                      name="case.insurance.claim"
                      label="Claim"
                      placeholder="Enter Claim"
                      props={{ className: 'bg-white' }}
                      required
                      maxLength={128}
                      allowSpecialCharacters={false}
                    />
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
        {addNewCourt && (
          <CreateCourtModal
            onClose={() => setAddNewCourt(false)}
            setUpdatedCourt={() => {
              setRefreshKey((prev) => prev + 1);
            }}
          />
        )}
      </div>
    </Loader>
  );
};
